import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { createContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { InfoCircleIcon } from '@/components/icons/InfoCircleIcon';
import { Lightbulb03Icon } from '@/components/icons/Lightbulb03Icon';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { DragAndDropList } from '@/components/lists/DragAndDropList/DragAndDropList';
import { DraggableListItemInnerComponentProps } from '@/components/lists/DragAndDropList/DraggableListItem.types';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { PopperContent } from '@/components/poppers/PopperContent';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { SideDrawer } from '@/modules/common/SideDrawer';
import { useRegisterSlideNavDirtyGetter } from '@/modules/presentation/clientPresentation/context/SlideNavGuard.context';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';
import { zIndexes } from '@/styles/zIndexes';
import { GRAND_TOTAL_COLUMN_ID } from '../BalanceSheetTable/BalanceSheetTable.columns';
import { useUnguardedBalanceSheetTableContext, ViewConfiguration } from '../BalanceSheetTable/BalanceSheetTableContext';
import { BalanceSheetTable_BalanceSheetFragment } from '../BalanceSheetTable/graphql/BalanceSheetTable.fragments.generated';
import { BalanceSheetConfigChat } from './BalanceSheetConfigChat';
import { BalanceSheetConfigColumnItem, StaticColumnRow } from './BalanceSheetConfigColumnItem';
import { BalanceSheetConfigColumn, RefetchQueryDescriptor, UpdateConfigurationFn } from './BalanceSheetConfigPanel.types';
import { computeOverflowColumns } from './BalanceSheetConfigPanel.utils';
import { useBalanceSheetConfigColumns } from './hooks/useBalanceSheetConfigColumns';
import { useBalanceSheetConfigPanelState } from './hooks/useBalanceSheetConfigPanelState';
export type { UpdateConfigurationFn } from './BalanceSheetConfigPanel.types';
const DEFAULT_Z_INDEX = zIndexes.MENU + 2;
const ColumnVisibilityContext = createContext<{
  hiddenColumns: Set<string>;
  overflowColumnIds: Set<string>;
  cutoffColumnId: string | null;
  hasOverflow: boolean;
  onVisibilityChange: (columnId: string, visible: boolean) => void;
} | null>(null);

// Keep the row component identity stable during a drag. Context supplies
// current visibility and overflow state without reading refs during render.
function ColumnItemWithVisibility(props) {
  const $ = _c(16);
  const {
    hiddenColumns,
    overflowColumnIds,
    cutoffColumnId,
    hasOverflow,
    onVisibilityChange
  } = useGuardedContext(ColumnVisibilityContext, "ColumnVisibilityContext");
  let t0;
  if ($[0] !== hiddenColumns || $[1] !== props.item.id) {
    t0 = hiddenColumns.has(props.item.id);
    $[0] = hiddenColumns;
    $[1] = props.item.id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = !t0;
  let t2;
  if ($[3] !== overflowColumnIds || $[4] !== props.item.id) {
    t2 = overflowColumnIds.has(props.item.id);
    $[3] = overflowColumnIds;
    $[4] = props.item.id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = cutoffColumnId === props.item.id;
  let t4;
  if ($[6] !== onVisibilityChange || $[7] !== props.item.id) {
    t4 = checked => onVisibilityChange(props.item.id, checked);
    $[6] = onVisibilityChange;
    $[7] = props.item.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hasOverflow || $[10] !== props || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <BalanceSheetConfigColumnItem {...props} checked={t1} isOverflow={t2} isCutoffItem={t3} hasOverflow={hasOverflow} onCheckedChange={t4} />;
    $[9] = hasOverflow;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
type ActiveView = 'main' | 'advanced';
export interface BalanceSheetConfigPanelProps {
  open: boolean;
  onClose: (restoredConfig?: ViewConfiguration) => void;
  balanceSheet: BalanceSheetTable_BalanceSheetFragment | null;
  /** Persist a configuration update to the backend */
  updateConfiguration: UpdateConfigurationFn;
  /** Queries to refetch after a mutation */
  refetchDocuments: RefetchQueryDescriptor[];
  /** Override the SideDrawer modal z-index. In-app defaults to zIndexes.MENU + 2 (above the
   *  sidebar but below modals); the presentation designer needs a higher value since it's
   *  already inside a modal. */
  zIndex?: number;
  /** Display name for the household (e.g. "Rogers Household") */
  householdName?: string;
  /** Whether the panel is being used in the presentation designer context */
  isPresentationMode?: boolean;
  /** Full presentation views allow name and insurance changes, but no layout changes. */
  isFullBalanceSheet?: boolean;
}
export function BalanceSheetConfigPanel(t0) {
  const $ = _c(187);
  const {
    open,
    onClose,
    balanceSheet,
    updateConfiguration,
    refetchDocuments,
    zIndex: t1,
    householdName: t2,
    isPresentationMode: t3,
    isFullBalanceSheet: t4
  } = t0;
  const zIndex = t1 === undefined ? DEFAULT_Z_INDEX : t1;
  const householdName = t2 === undefined ? "" : t2;
  const isPresentationMode = t3 === undefined ? false : t3;
  const isFullBalanceSheet = t4 === undefined ? false : t4;
  const [activeView, setActiveView] = useState("main");
  const {
    inEstateColumns,
    inEstateTotalColumn,
    outOfEstateColumns,
    outOfEstateTotalColumn
  } = useBalanceSheetConfigColumns(balanceSheet);
  let t5;
  if ($[0] !== inEstateColumns || $[1] !== inEstateTotalColumn || $[2] !== isFullBalanceSheet || $[3] !== onClose || $[4] !== open || $[5] !== outOfEstateColumns || $[6] !== outOfEstateTotalColumn || $[7] !== refetchDocuments || $[8] !== updateConfiguration) {
    t5 = {
      open,
      saveOnDone: isFullBalanceSheet,
      onClose,
      inEstateColumns,
      inEstateTotalColumn,
      outOfEstateColumns,
      outOfEstateTotalColumn,
      updateConfiguration,
      refetchDocuments
    };
    $[0] = inEstateColumns;
    $[1] = inEstateTotalColumn;
    $[2] = isFullBalanceSheet;
    $[3] = onClose;
    $[4] = open;
    $[5] = outOfEstateColumns;
    $[6] = outOfEstateTotalColumn;
    $[7] = refetchDocuments;
    $[8] = updateConfiguration;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    localConfig,
    hiddenColumns,
    hasUnsavedChanges,
    showDiscardConfirmation,
    orderedInEstate,
    orderedOutOfEstate,
    handleSwitchChange,
    handleCheckboxChange,
    handleDisplayNameChange,
    handleColumnVisibilityChange,
    handleInEstateDropped,
    handleOutOfEstateDropped,
    handleSave,
    handleCancel,
    executeCancel,
    handleDone,
    handleDiscardConfirmation,
    applyConfigPreset,
    undoLastChange,
    configHistoryLength,
    appliedSuggestions,
    isSuggestionThinking
  } = useBalanceSheetConfigPanelState(t5);
  let t6;
  let t7;
  if ($[10] !== open) {
    t6 = () => {
      if (open) {
        setActiveView("main");
      }
    };
    t7 = [open, "main"];
    $[10] = open;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  useRegisterSlideNavDirtyGetter(open && hasUnsavedChanges, executeCancel);
  const handleCloseOrCancel = handleCancel;
  let t8;
  if ($[13] !== activeView) {
    t8 = activeView === "advanced" ? <Stack direction="row" alignItems="center" gap={1}><IconButton icon={ChevronLeftIcon} ariaLabel="Back to suggestions" size="xs" variant="transparent" onClick={() => setActiveView("main")} />Manage balance sheet</Stack> : "Manage balance sheet";
    $[13] = activeView;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const heading = t8;
  const tableContext = useUnguardedBalanceSheetTableContext();
  const maxVisibleColumns = tableContext?.maxVisibleColumns ?? null;
  let t9;
  if ($[15] !== balanceSheet?.columns) {
    t9 = balanceSheet?.columns.some(_temp) ?? false;
    $[15] = balanceSheet?.columns;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const hasGrandTotalColumn = t9;
  localConfig?.showInEstate;
  localConfig?.showOutOfEstate;
  const t10 = localConfig?.showInEstate ?? false;
  const t11 = localConfig?.showOutOfEstate ?? false;
  let t12;
  if ($[17] !== hasGrandTotalColumn || $[18] !== hiddenColumns || $[19] !== inEstateTotalColumn || $[20] !== maxVisibleColumns || $[21] !== orderedInEstate || $[22] !== orderedOutOfEstate || $[23] !== outOfEstateTotalColumn || $[24] !== t10 || $[25] !== t11) {
    t12 = computeOverflowColumns({
      maxVisibleColumns,
      showInEstate: t10,
      showOutOfEstate: t11,
      inEstateTotalColumn,
      outOfEstateTotalColumn,
      hasGrandTotalColumn,
      orderedInEstate,
      orderedOutOfEstate,
      hiddenColumns
    });
    $[17] = hasGrandTotalColumn;
    $[18] = hiddenColumns;
    $[19] = inEstateTotalColumn;
    $[20] = maxVisibleColumns;
    $[21] = orderedInEstate;
    $[22] = orderedOutOfEstate;
    $[23] = outOfEstateTotalColumn;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const {
    overflowColumnIds,
    hasOverflow,
    cutoffColumnId
  } = t12;
  const hasInEstateColumns = (inEstateColumns.length > 0 || inEstateTotalColumn) && localConfig?.showInEstate;
  const hasOutOfEstateColumns = (outOfEstateColumns.length > 0 || outOfEstateTotalColumn) && localConfig?.showOutOfEstate;
  let t13;
  bb0: {
    if (!localConfig) {
      let t14;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <></>;
        $[27] = t14;
      } else {
        t14 = $[27];
      }
      t13 = t14;
      break bb0;
    }
    if (activeView === "main") {
      let t14;
      if ($[28] !== isFullBalanceSheet || $[29] !== isPresentationMode) {
        t14 = isPresentationMode && !isFullBalanceSheet && <Stack sx={{
          p: 1.5,
          gap: 1,
          borderRadius: "8px",
          border: `1px solid ${COLORS.NAVY[300]}`,
          backgroundColor: COLORS.NAVY[50]
        }}><Stack gap={0.5}><Lightbulb03Icon size={16} /><Typography variant="title-s">Balance sheets in presentations</Typography></Stack><Typography variant="body-xs">Balance sheets in presentations can display a maximum of 8 columns, including section totals. If your client's entities exceed this number, some entities may be hidden or consolidated.</Typography></Stack>;
        $[28] = isFullBalanceSheet;
        $[29] = isPresentationMode;
        $[30] = t14;
      } else {
        t14 = $[30];
      }
      let t15;
      if ($[31] !== handleDisplayNameChange || $[32] !== isPresentationMode || $[33] !== localConfig.displayName) {
        t15 = isPresentationMode && <TextInput label="Name" value={localConfig.displayName ?? ""} onChange={e => handleDisplayNameChange(e.target.value)} />;
        $[31] = handleDisplayNameChange;
        $[32] = isPresentationMode;
        $[33] = localConfig.displayName;
        $[34] = t15;
      } else {
        t15 = $[34];
      }
      let t16;
      if ($[35] !== householdName || $[36] !== isPresentationMode) {
        t16 = !isPresentationMode && <Typography variant="title-s">{householdName} balance sheet</Typography>;
        $[35] = householdName;
        $[36] = isPresentationMode;
        $[37] = t16;
      } else {
        t16 = $[37];
      }
      let t17;
      if ($[38] !== handleCheckboxChange || $[39] !== isFullBalanceSheet || $[40] !== isPresentationMode || $[41] !== localConfig.showExpandedAssetCategories || $[42] !== localConfig.showSummaryMetrics) {
        t17 = !isFullBalanceSheet && <><Checkbox label={isPresentationMode ? "Display summary metrics at top of page" : "Show summary metrics"} name="showSummaryMetrics" value={localConfig.showSummaryMetrics} onChange={handleCheckboxChange("showSummaryMetrics")} /><Checkbox label={isPresentationMode ? "Expand asset categories" : "Show expanded asset categories"} name="showExpandedAssetCategories" value={localConfig.showExpandedAssetCategories} onChange={handleCheckboxChange("showExpandedAssetCategories")} /></>;
        $[38] = handleCheckboxChange;
        $[39] = isFullBalanceSheet;
        $[40] = isPresentationMode;
        $[41] = localConfig.showExpandedAssetCategories;
        $[42] = localConfig.showSummaryMetrics;
        $[43] = t17;
      } else {
        t17 = $[43];
      }
      const t18 = localConfig.includeDeathBenefitInTotals;
      let t19;
      if ($[44] !== handleSwitchChange) {
        t19 = handleSwitchChange("includeDeathBenefitInTotals");
        $[44] = handleSwitchChange;
        $[45] = t19;
      } else {
        t19 = $[45];
      }
      let t20;
      if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = <PopperContent body="When enabled, balance sheet totals and summary metrics reflect the life insurance death benefit. When disabled, they reflect the cash value instead." />;
        $[46] = t20;
      } else {
        t20 = $[46];
      }
      let t21;
      if ($[47] !== localConfig.includeDeathBenefitInTotals || $[48] !== t19) {
        t21 = <Switch label="Include death benefit in totals" labelPosition="right" name="includeDeathBenefitInTotals" value={t18} onChange={t19} contextualHelp={t20} />;
        $[47] = localConfig.includeDeathBenefitInTotals;
        $[48] = t19;
        $[49] = t21;
      } else {
        t21 = $[49];
      }
      let t22;
      if ($[50] !== t17 || $[51] !== t21) {
        t22 = <Stack gap={1}>{t17}{t21}</Stack>;
        $[50] = t17;
        $[51] = t21;
        $[52] = t22;
      } else {
        t22 = $[52];
      }
      let t23;
      if ($[53] !== t16 || $[54] !== t22) {
        t23 = <Stack gap={1.5}>{t16}{t22}</Stack>;
        $[53] = t16;
        $[54] = t22;
        $[55] = t23;
      } else {
        t23 = $[55];
      }
      let t24;
      if ($[56] !== appliedSuggestions || $[57] !== applyConfigPreset || $[58] !== configHistoryLength || $[59] !== isFullBalanceSheet || $[60] !== isSuggestionThinking || $[61] !== localConfig || $[62] !== undoLastChange) {
        t24 = !isFullBalanceSheet && <BalanceSheetConfigChat localConfig={localConfig} configHistoryLength={configHistoryLength} appliedSuggestions={appliedSuggestions} isSuggestionThinking={isSuggestionThinking} onApplyPreset={applyConfigPreset} onUndo={undoLastChange} />;
        $[56] = appliedSuggestions;
        $[57] = applyConfigPreset;
        $[58] = configHistoryLength;
        $[59] = isFullBalanceSheet;
        $[60] = isSuggestionThinking;
        $[61] = localConfig;
        $[62] = undoLastChange;
        $[63] = t24;
      } else {
        t24 = $[63];
      }
      let t25;
      if ($[64] !== t14 || $[65] !== t15 || $[66] !== t23 || $[67] !== t24) {
        t25 = <Stack gap={2.5} width="100%">{t14}{t15}{t23}{t24}</Stack>;
        $[64] = t14;
        $[65] = t15;
        $[66] = t23;
        $[67] = t24;
        $[68] = t25;
      } else {
        t25 = $[68];
      }
      t13 = t25;
      break bb0;
    }
    let t14;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Typography variant="title-s">Select and order entities</Typography>;
      $[69] = t14;
    } else {
      t14 = $[69];
    }
    const t15 = localConfig.showInEstate;
    let t16;
    if ($[70] !== handleSwitchChange) {
      t16 = handleSwitchChange("showInEstate");
      $[70] = handleSwitchChange;
      $[71] = t16;
    } else {
      t16 = $[71];
    }
    let t17;
    if ($[72] !== localConfig.showInEstate || $[73] !== t16) {
      t17 = <Switch label="Show in estate" labelPosition="right" name="showInEstate" value={t15} onChange={t16} />;
      $[72] = localConfig.showInEstate;
      $[73] = t16;
      $[74] = t17;
    } else {
      t17 = $[74];
    }
    const t18 = localConfig.separateInEstateDirectlyHeld;
    let t19;
    if ($[75] !== handleCheckboxChange) {
      t19 = handleCheckboxChange("separateInEstateDirectlyHeld");
      $[75] = handleCheckboxChange;
      $[76] = t19;
    } else {
      t19 = $[76];
    }
    const t20 = !localConfig.showInEstate;
    let t21;
    if ($[77] !== localConfig.separateInEstateDirectlyHeld || $[78] !== t19 || $[79] !== t20) {
      t21 = <Checkbox label="Separate directly held" name="separateInEstateDirectlyHeld" value={t18} onChange={t19} disabled={t20} />;
      $[77] = localConfig.separateInEstateDirectlyHeld;
      $[78] = t19;
      $[79] = t20;
      $[80] = t21;
    } else {
      t21 = $[80];
    }
    const t22 = localConfig.separateInEstateTrusts;
    let t23;
    if ($[81] !== handleCheckboxChange) {
      t23 = handleCheckboxChange("separateInEstateTrusts");
      $[81] = handleCheckboxChange;
      $[82] = t23;
    } else {
      t23 = $[82];
    }
    const t24 = !localConfig.showInEstate;
    let t25;
    if ($[83] !== localConfig.separateInEstateTrusts || $[84] !== t23 || $[85] !== t24) {
      t25 = <Checkbox label="Separate in-estate trusts" name="separateInEstateTrusts" value={t22} onChange={t23} disabled={t24} />;
      $[83] = localConfig.separateInEstateTrusts;
      $[84] = t23;
      $[85] = t24;
      $[86] = t25;
    } else {
      t25 = $[86];
    }
    let t26;
    if ($[87] !== t21 || $[88] !== t25) {
      t26 = <Stack gap={1.5}>{t21}{t25}</Stack>;
      $[87] = t21;
      $[88] = t25;
      $[89] = t26;
    } else {
      t26 = $[89];
    }
    let t27;
    if ($[90] !== t17 || $[91] !== t26) {
      t27 = <Stack flex={1} gap={2}>{t17}{t26}</Stack>;
      $[90] = t17;
      $[91] = t26;
      $[92] = t27;
    } else {
      t27 = $[92];
    }
    const t28 = localConfig.showOutOfEstate;
    let t29;
    if ($[93] !== handleSwitchChange) {
      t29 = handleSwitchChange("showOutOfEstate");
      $[93] = handleSwitchChange;
      $[94] = t29;
    } else {
      t29 = $[94];
    }
    let t30;
    if ($[95] !== localConfig.showOutOfEstate || $[96] !== t29) {
      t30 = <Switch label="Show out of estate" labelPosition="right" name="showOutOfEstate" value={t28} onChange={t29} />;
      $[95] = localConfig.showOutOfEstate;
      $[96] = t29;
      $[97] = t30;
    } else {
      t30 = $[97];
    }
    const t31 = localConfig.separateOutOfEstateTrusts;
    let t32;
    if ($[98] !== handleCheckboxChange) {
      t32 = handleCheckboxChange("separateOutOfEstateTrusts");
      $[98] = handleCheckboxChange;
      $[99] = t32;
    } else {
      t32 = $[99];
    }
    const t33 = !localConfig.showOutOfEstate;
    let t34;
    if ($[100] !== localConfig.separateOutOfEstateTrusts || $[101] !== t32 || $[102] !== t33) {
      t34 = <Stack gap={1.5}><Checkbox label="Separate out-of-estate trusts" name="separateOutOfEstateTrusts" value={t31} onChange={t32} disabled={t33} /></Stack>;
      $[100] = localConfig.separateOutOfEstateTrusts;
      $[101] = t32;
      $[102] = t33;
      $[103] = t34;
    } else {
      t34 = $[103];
    }
    let t35;
    if ($[104] !== t30 || $[105] !== t34) {
      t35 = <Stack flex={1} gap={2}>{t30}{t34}</Stack>;
      $[104] = t30;
      $[105] = t34;
      $[106] = t35;
    } else {
      t35 = $[106];
    }
    let t36;
    if ($[107] !== t27 || $[108] !== t35) {
      t36 = <Stack direction="row" gap={2}>{t27}{t35}</Stack>;
      $[107] = t27;
      $[108] = t35;
      $[109] = t36;
    } else {
      t36 = $[109];
    }
    let t37;
    if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <Box sx={{
        height: "1px",
        backgroundColor: COLORS.GRAY[400],
        mb: 1
      }} />;
      $[110] = t37;
    } else {
      t37 = $[110];
    }
    let t38;
    let t39;
    if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
      t38 = {
        borderBottom: `1px solid ${COLORS.GRAY[200]}`,
        pb: 1.5,
        pt: 1,
        px: 0.5
      };
      t39 = <Typography variant="title-s">Balance sheet columns</Typography>;
      $[111] = t38;
      $[112] = t39;
    } else {
      t38 = $[111];
      t39 = $[112];
    }
    let t40;
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = {
        display: "flex",
        alignItems: "center"
      };
      $[113] = t40;
    } else {
      t40 = $[113];
    }
    let t41;
    if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <Stack direction="row" alignItems="center" gap={1} sx={t38}>{t39}<Tooltip title="Drag to reorder how columns appear in the balance sheet. Total columns are always pinned at the end of their group." placement="top"><Box sx={t40}><InfoCircleIcon size={16} sx={{
              color: COLORS.TEXT.SECONDARY
            }} /></Box></Tooltip></Stack>;
      $[114] = t41;
    } else {
      t41 = $[114];
    }
    let t42;
    if ($[115] !== handleInEstateDropped || $[116] !== hasInEstateColumns || $[117] !== hasOverflow || $[118] !== inEstateColumns || $[119] !== inEstateTotalColumn) {
      t42 = hasInEstateColumns && <Box>{inEstateColumns.length > 0 && <DragAndDropList key={inEstateColumns.map(_temp2).join(",")} items={inEstateColumns} ItemInnerComponent={ColumnItemWithVisibility} onDropped={handleInEstateDropped} respondToLengthChanges={false} withDividers={!hasOverflow} />}{inEstateTotalColumn && <Box sx={{
          ...(inEstateColumns.length > 0 && !hasOverflow && {
            borderTop: `1px solid ${COLORS.GRAY[200]}`
          }),
          borderBottom: `1px solid ${COLORS.GRAY[200]}`
        }}><StaticColumnRow item={inEstateTotalColumn} showCheckbox={true} /></Box>}</Box>;
      $[115] = handleInEstateDropped;
      $[116] = hasInEstateColumns;
      $[117] = hasOverflow;
      $[118] = inEstateColumns;
      $[119] = inEstateTotalColumn;
      $[120] = t42;
    } else {
      t42 = $[120];
    }
    let t43;
    if ($[121] !== hasInEstateColumns || $[122] !== hasOutOfEstateColumns) {
      t43 = hasInEstateColumns && hasOutOfEstateColumns && <Box sx={{
        height: "1px",
        backgroundColor: COLORS.GRAY[200]
      }} />;
      $[121] = hasInEstateColumns;
      $[122] = hasOutOfEstateColumns;
      $[123] = t43;
    } else {
      t43 = $[123];
    }
    let t44;
    if ($[124] !== handleOutOfEstateDropped || $[125] !== hasOutOfEstateColumns || $[126] !== hasOverflow || $[127] !== outOfEstateColumns || $[128] !== outOfEstateTotalColumn) {
      t44 = hasOutOfEstateColumns && <Box>{outOfEstateColumns.length > 0 && <DragAndDropList key={outOfEstateColumns.map(_temp3).join(",")} items={outOfEstateColumns} ItemInnerComponent={ColumnItemWithVisibility} onDropped={handleOutOfEstateDropped} respondToLengthChanges={false} withDividers={!hasOverflow} />}{outOfEstateTotalColumn && <Box sx={{
          ...(outOfEstateColumns.length > 0 && !hasOverflow && {
            borderTop: `1px solid ${COLORS.GRAY[200]}`
          }),
          borderBottom: `1px solid ${COLORS.GRAY[200]}`
        }}><StaticColumnRow item={outOfEstateTotalColumn} showCheckbox={true} /></Box>}</Box>;
      $[124] = handleOutOfEstateDropped;
      $[125] = hasOutOfEstateColumns;
      $[126] = hasOverflow;
      $[127] = outOfEstateColumns;
      $[128] = outOfEstateTotalColumn;
      $[129] = t44;
    } else {
      t44 = $[129];
    }
    let t45;
    if ($[130] !== hasOverflow) {
      t45 = hasOverflow && <Box sx={{
        pt: 1.5
      }}><Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>*These columns sit outside the maximum number that can be displayed, and are hidden from view.</Typography></Box>;
      $[130] = hasOverflow;
      $[131] = t45;
    } else {
      t45 = $[131];
    }
    let t46;
    if ($[132] !== t42 || $[133] !== t43 || $[134] !== t44 || $[135] !== t45) {
      t46 = <Stack gap="2px">{t37}{t41}{t42}{t43}{t44}{t45}</Stack>;
      $[132] = t42;
      $[133] = t43;
      $[134] = t44;
      $[135] = t45;
      $[136] = t46;
    } else {
      t46 = $[136];
    }
    let t47;
    if ($[137] !== t36 || $[138] !== t46) {
      t47 = <Stack gap={2} width="100%">{t14}{t36}{t46}</Stack>;
      $[137] = t36;
      $[138] = t46;
      $[139] = t47;
    } else {
      t47 = $[139];
    }
    t13 = t47;
  }
  const body = t13;
  let t14;
  bb1: {
    if (!localConfig) {
      t14 = undefined;
      break bb1;
    }
    if (activeView === "main") {
      const t15 = isFullBalanceSheet ? "flex-end" : "space-between";
      let t16;
      if ($[140] !== isFullBalanceSheet) {
        t16 = !isFullBalanceSheet && <Button variant="secondary" size="sm" startIcon={Settings04Icon} onClick={() => setActiveView("advanced")}>Advanced settings</Button>;
        $[140] = isFullBalanceSheet;
        $[141] = t16;
      } else {
        t16 = $[141];
      }
      let t17;
      if ($[142] !== executeCancel) {
        t17 = <Button variant="secondary" size="sm" onClick={executeCancel}>Cancel</Button>;
        $[142] = executeCancel;
        $[143] = t17;
      } else {
        t17 = $[143];
      }
      let t18;
      if ($[144] !== handleDone) {
        t18 = <Button variant="primary" size="sm" onClick={handleDone}>Done</Button>;
        $[144] = handleDone;
        $[145] = t18;
      } else {
        t18 = $[145];
      }
      let t19;
      if ($[146] !== t17 || $[147] !== t18) {
        t19 = <Stack direction="row" gap={1.5}>{t17}{t18}</Stack>;
        $[146] = t17;
        $[147] = t18;
        $[148] = t19;
      } else {
        t19 = $[148];
      }
      let t20;
      if ($[149] !== t15 || $[150] !== t16 || $[151] !== t19) {
        t20 = <Stack direction="row" justifyContent={t15} alignItems="center">{t16}{t19}</Stack>;
        $[149] = t15;
        $[150] = t16;
        $[151] = t19;
        $[152] = t20;
      } else {
        t20 = $[152];
      }
      t14 = t20;
      break bb1;
    }
    let t15;
    if ($[153] !== handleCancel) {
      t15 = <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>;
      $[153] = handleCancel;
      $[154] = t15;
    } else {
      t15 = $[154];
    }
    let t16;
    if ($[155] !== handleSave) {
      t16 = <Button variant="primary" size="sm" onClick={handleSave}>Save changes</Button>;
      $[155] = handleSave;
      $[156] = t16;
    } else {
      t16 = $[156];
    }
    let t17;
    if ($[157] !== t15 || $[158] !== t16) {
      t17 = <Stack direction="row" justifyContent="flex-end" gap={1.5}>{t15}{t16}</Stack>;
      $[157] = t15;
      $[158] = t16;
      $[159] = t17;
    } else {
      t17 = $[159];
    }
    t14 = t17;
  }
  const footer = t14;
  let t15;
  if ($[160] !== cutoffColumnId || $[161] !== handleColumnVisibilityChange || $[162] !== hasOverflow || $[163] !== hiddenColumns || $[164] !== overflowColumnIds) {
    t15 = {
      hiddenColumns,
      overflowColumnIds,
      cutoffColumnId,
      hasOverflow,
      onVisibilityChange: handleColumnVisibilityChange
    };
    $[160] = cutoffColumnId;
    $[161] = handleColumnVisibilityChange;
    $[162] = hasOverflow;
    $[163] = hiddenColumns;
    $[164] = overflowColumnIds;
    $[165] = t15;
  } else {
    t15 = $[165];
  }
  let t16;
  if ($[166] !== activeView) {
    t16 = activeView === "main" ? {
      pl: 1
    } : undefined;
    $[166] = activeView;
    $[167] = t16;
  } else {
    t16 = $[167];
  }
  let t17;
  if ($[168] !== zIndex) {
    t17 = {
      style: {
        zIndex
      }
    };
    $[168] = zIndex;
    $[169] = t17;
  } else {
    t17 = $[169];
  }
  let t18;
  if ($[170] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      px: 3,
      py: 2
    };
    $[170] = t18;
  } else {
    t18 = $[170];
  }
  let t19;
  if ($[171] !== body || $[172] !== footer || $[173] !== handleCloseOrCancel || $[174] !== heading || $[175] !== open || $[176] !== t16 || $[177] !== t17) {
    t19 = <SideDrawer isOpen={open} onClose={handleCloseOrCancel} width={OVERLAY_MD} heading={heading} headerProps={t16} modalProps={t17} bodyPropsSx={t18} body={body} footer={footer} />;
    $[171] = body;
    $[172] = footer;
    $[173] = handleCloseOrCancel;
    $[174] = heading;
    $[175] = open;
    $[176] = t16;
    $[177] = t17;
    $[178] = t19;
  } else {
    t19 = $[178];
  }
  let t20;
  if ($[179] !== handleDiscardConfirmation || $[180] !== showDiscardConfirmation || $[181] !== zIndex) {
    t20 = showDiscardConfirmation && <ConfirmationModal isOpen={true} heading="Unsaved changes" cancelText="Discard changes" confirmText="Stay" cancelButtonProps={{
      variant: "destructive-primary"
    }} confirmButtonProps={{
      variant: "secondary"
    }} closeOnOutsideClick={false} onClose={handleDiscardConfirmation} sx={{
      zIndex: zIndex + 100
    }}>You have unsaved changes. Are you sure you want to continue? Your changes will not be saved.</ConfirmationModal>;
    $[179] = handleDiscardConfirmation;
    $[180] = showDiscardConfirmation;
    $[181] = zIndex;
    $[182] = t20;
  } else {
    t20 = $[182];
  }
  let t21;
  if ($[183] !== t15 || $[184] !== t19 || $[185] !== t20) {
    t21 = <ColumnVisibilityContext.Provider value={t15}>{t19}{t20}</ColumnVisibilityContext.Provider>;
    $[183] = t15;
    $[184] = t19;
    $[185] = t20;
    $[186] = t21;
  } else {
    t21 = $[186];
  }
  return t21;
}
function _temp3(c_0) {
  return c_0.id;
}
function _temp2(c) {
  return c.id;
}
function _temp(column) {
  return column.ID === GRAND_TOTAL_COLUMN_ID;
}